// Review: everything waiting for a person. Open proposals to apply or dismiss,
// template drafts to publish, and disabled event types to enable.
// Components go in src/features/proposals.
import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowRight, FileCode2, FileText, Lightbulb, PowerOff, Puzzle, Zap } from 'lucide-react'
import { useMemo, type ReactNode } from 'react'
import { openProposalsQuery } from '@/api/queries/proposals'
import type { ProposalKind } from '@/api/types'
import { EmptyState } from '@/components/EmptyState'
import { PageHeader } from '@/components/PageHeader'
import { ErrorState } from '@/components/QueryState'
import { RelativeTime } from '@/components/RelativeTime'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardHeader } from '@/components/ui/card'
import { Segmented } from '@/components/ui/segmented'
import { SkeletonText } from '@/components/ui/skeleton'
import { AppName } from '@/features/events/AppName'
import { ViaBadge } from '@/features/ops/AuditEntryRow'
import { groupByTarget, isProposalKind, KIND_OPTIONS } from '@/features/proposals/model'
import { ProposalRow } from '@/features/proposals/ProposalRow'
import { disabledEventTypesQuery, templateDraftsQuery } from '@/features/proposals/review-api'
import { FORMAT_LABEL, OwnerLabel } from '@/features/templates/bits'
import { useAppScope } from '@/shell/useAppScope'

interface ReviewSearch {
  /** Show only proposals of this kind. */
  kind?: ProposalKind
}

export const Route = createFileRoute('/review')({
  staticData: { crumb: 'Review' },
  validateSearch: (s: Record<string, unknown>): ReviewSearch => ({
    kind: isProposalKind(s.kind) ? s.kind : undefined,
  }),
  component: ReviewPage,
})

const KIND_FILTER = [{ value: 'all', label: 'All' }, ...KIND_OPTIONS] as const

function ReviewPage() {
  const { appId, application } = useAppScope()
  const { kind } = Route.useSearch()
  const navigate = Route.useNavigate()
  const proposals = useQuery(openProposalsQuery({ app: appId, includeShared: true, kind }))
  const drafts = useQuery(templateDraftsQuery(appId))
  const disabled = useQuery(disabledEventTypesQuery(appId))
  const groups = useMemo(() => groupByTarget(proposals.data?.items ?? []), [proposals.data])
  const scope = application ? `${application.name} and the shared library` : 'every application'

  return (
    <>
      <PageHeader
        title="Review"
        description={`Changes waiting for a person in ${scope}: proposals to apply or dismiss, template drafts to publish, and disabled event types to enable, including changes made through Claude.`}
      />
      <div className="space-y-5">
        <Section
          title="Proposals"
          description="Suggested rule, settings, and partial changes. Review one to load it into its editor, then save it to apply it or dismiss it."
          count={proposals.data?.items.length}
          more={proposals.data?.more}
          actions={
            <Segmented
              aria-label="Filter by kind"
              value={kind ?? 'all'}
              options={KIND_FILTER}
              onValueChange={(v) =>
                void navigate({
                  search: (prev) => ({ ...prev, kind: v === 'all' ? undefined : v }),
                  replace: true,
                })
              }
            />
          }
          query={proposals}
          empty={
            <EmptyState
              compact
              icon={Lightbulb}
              title={kind ? 'No open proposals of this kind' : 'No open proposals'}
              description="When someone, or Claude, proposes a change to rules, event type settings, or a partial, it waits here until a person saves or dismisses it."
            />
          }
        >
          <ul className="divide-y divide-line">
            {groups.map((g) => (
              <li key={g.targetId} className="px-4 py-3">
                <TargetHeading group={g} />
                <ul className="mt-2 space-y-3 border-l-2 border-brass-line/40 pl-3">
                  {g.proposals.map((p) => (
                    <li key={p.id}>
                      <ProposalRow proposal={p} />
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </Section>

        <Section
          title="Template drafts"
          description="Email and document templates with a saved draft that isn’t published. Compare it with the active version, then publish or discard it."
          count={drafts.data?.length}
          query={drafts}
          empty={
            <EmptyState
              compact
              icon={FileCode2}
              title="No unpublished drafts"
              description="Every template’s draft is published or discarded. Sends always use a template’s active version, never its draft."
            />
          }
        >
          <ul className="divide-y divide-line">
            {(drafts.data ?? []).map((t) => (
              <li key={t.id} className="flex items-start gap-3 px-4 py-3">
                {t.kind === 'document' ? (
                  <FileText className="mt-0.5 size-4 shrink-0 text-ink-3" aria-hidden />
                ) : (
                  <FileCode2 className="mt-0.5 size-4 shrink-0 text-ink-3" aria-hidden />
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-1.5 text-sm">
                    <span className="font-medium text-ink">{t.name}</span>
                    <code className="font-mono text-[12.5px] text-ink-2">{t.key}</code>
                    {t.kind === 'document' && (
                      <Badge tone="outline">Document · {FORMAT_LABEL[t.format]}</Badge>
                    )}
                    {t.active_version == null ? (
                      <Badge tone="outline">Never published</Badge>
                    ) : (
                      <Badge tone="neutral">Active: v{t.active_version}</Badge>
                    )}
                  </div>
                  <Byline
                    owner={<OwnerLabel applicationId={t.application_id} />}
                    action="Draft saved"
                    who={t.draft_updated_by}
                    via={t.draft_via}
                    when={t.draft_updated_at}
                  />
                </div>
                {t.active_version_id ? (
                  <Button asChild size="sm" className="mt-0.5">
                    <Link
                      to="/templates/$templateId/versions"
                      params={{ templateId: t.id }}
                      search={{ from: t.active_version_id, to: 'draft' }}
                    >
                      Compare
                      <ArrowRight />
                    </Link>
                  </Button>
                ) : (
                  <Button asChild size="sm" className="mt-0.5">
                    <Link to="/templates/$templateId" params={{ templateId: t.id }}>
                      Open
                      <ArrowRight />
                    </Link>
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </Section>

        <Section
          title="Disabled event types"
          description="A disabled event type rejects every event, so nothing is received or sent. Review its rules and settings, then enable it on its page."
          count={disabled.data?.length}
          query={disabled}
          empty={
            <EmptyState
              compact
              icon={PowerOff}
              title="No disabled event types"
              description="Every event type in scope is enabled. New event types Claude creates start disabled and appear here."
            />
          }
        >
          <ul className="divide-y divide-line">
            {(disabled.data ?? []).map((e) => (
              <li key={e.id} className="flex items-start gap-3 px-4 py-3">
                <Zap className="mt-0.5 size-4 shrink-0 text-ink-3" aria-hidden />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-1.5 text-sm">
                    <code className="font-mono text-[13px] font-medium text-ink">{e.key}</code>
                    <span className="text-ink-2">{e.name}</span>
                    {e.rule_count === 0 ? (
                      <Badge tone="warning">No rules</Badge>
                    ) : (
                      <Badge tone="neutral">
                        {e.rule_count === 1 ? '1 rule' : `${e.rule_count} rules`}
                      </Badge>
                    )}
                  </div>
                  <Byline
                    owner={<AppName id={e.application_id} className="inline" />}
                    action="Created"
                    who={e.created_by}
                    via={e.created_via}
                    when={e.created_at}
                  />
                </div>
                <Button asChild size="sm" className="mt-0.5">
                  <Link to="/event-types/$eventTypeId" params={{ eventTypeId: e.id }}>
                    Open
                    <ArrowRight />
                  </Link>
                </Button>
              </li>
            ))}
          </ul>
        </Section>
      </div>
    </>
  )
}

function Section<T>({
  title,
  description,
  count,
  more,
  actions,
  query,
  empty,
  children,
}: {
  title: string
  description: string
  count: number | undefined
  /** The list was cut short. */
  more?: boolean
  actions?: ReactNode
  query: UseQueryResult<T>
  empty: ReactNode
  children: ReactNode
}) {
  return (
    <Card>
      <CardHeader
        className="flex-wrap border-b border-line pb-3"
        title={
          <span className="flex items-center gap-2">
            {title}
            {count !== undefined && (
              <span className="tabular text-xs font-normal text-ink-3">
                {more ? `${count}+` : count}
              </span>
            )}
          </span>
        }
        description={description}
        actions={actions}
      />
      {query.isPending ? (
        <SkeletonText className="p-4" />
      ) : query.error ? (
        <ErrorState compact error={query.error} onRetry={() => void query.refetch()} />
      ) : count === 0 ? (
        empty
      ) : (
        children
      )}
    </Card>
  )
}

function TargetHeading({ group: g }: { group: ReturnType<typeof groupByTarget>[number] }) {
  const Icon = g.kind === 'partial' ? Puzzle : Zap
  const label = g.target.ref ?? g.target.name ?? g.targetId
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <Icon className="size-4 shrink-0 text-ink-3" aria-hidden />
      {!g.target.exists ? (
        <span className="font-mono text-[12.5px] text-ink-3 line-through">{g.targetId}</span>
      ) : g.kind === 'partial' ? (
        <Link
          to="/partials/$partialId"
          params={{ partialId: g.targetId }}
          className="font-mono text-[13px] font-medium text-ink hover:text-brass hover:underline"
        >
          {label}
        </Link>
      ) : (
        <Link
          to="/event-types/$eventTypeId"
          params={{ eventTypeId: g.targetId }}
          className="font-mono text-[13px] font-medium text-ink hover:text-brass hover:underline"
        >
          {label}
        </Link>
      )}
      {g.target.exists && g.target.name && g.target.name !== label && (
        <span className="text-ink-2">{g.target.name}</span>
      )}
      {!g.target.exists && <Badge tone="danger">Deleted</Badge>}
    </div>
  )
}

/** "Shop · Draft saved by Ann [via Claude] · 2h ago" */
function Byline({
  owner,
  action,
  who,
  via,
  when,
}: {
  owner: ReactNode
  action: string
  who: string | null | undefined
  via: string | null | undefined
  when: string | null | undefined
}) {
  return (
    <p className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-ink-2">
      {owner}
      <span className="text-line-strong" aria-hidden>
        ·
      </span>
      <span>
        {action}
        {who ? ` by ${who}` : ''}
      </span>
      {via && <ViaBadge via={via} />}
      <span className="text-line-strong" aria-hidden>
        ·
      </span>
      <RelativeTime value={when} />
    </p>
  )
}
